import { formatTrustBudDateLabel } from "../../shared/utils/trustbudDates";

export function formatRelativeDayWord(timestamp) {
  if (!timestamp) return "recently";
  const last = new Date(timestamp).getTime();
  if (Number.isNaN(last)) return "recently";
  const days = Math.floor((Date.now() - last) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

export function formatRelativeTime(timestamp) {
  if (!timestamp) return "";
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMinutes = Math.round((now - then) / 60000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays}d ago`;
}

export function formatDateLabel(value) {
  return formatTrustBudDateLabel(value);
}

function normalizeMode(value) {
  return value === "challenge" ? "challenge" : "support";
}

export function resolveSprintState({ explicitState, reflectionToday, viewerActedToday = null, partnerActedToday = null }) {
  const viewerUpdatedToday = viewerActedToday ?? Boolean(
    reflectionToday?.viewer_updated || reflectionToday?.viewer_noted || reflectionToday?.viewer_reflected
  );
  const partnerUpdatedToday = partnerActedToday ?? Boolean(
    reflectionToday?.other_updated || reflectionToday?.other_noted || reflectionToday?.other_reflected
  );

  if (viewerUpdatedToday || partnerUpdatedToday) {
    return "active";
  }
  if (["drift", "reentry", "dormant"].includes(explicitState)) {
    return explicitState;
  }
  return "active";
}

export function resolveSprintStateCopy({
  explicitState,
  reflectionToday,
  lastEvent,
  partnerName,
  viewerActedToday = null,
  partnerActedToday = null,
  sprintMode = "support",
  sprintWindow = null,
  canRenew = false,
}) {
  const viewerUpdatedToday = viewerActedToday ?? Boolean(
    reflectionToday?.viewer_updated || reflectionToday?.viewer_noted || reflectionToday?.viewer_reflected
  );
  const partnerUpdatedToday = partnerActedToday ?? Boolean(
    reflectionToday?.other_updated || reflectionToday?.other_noted || reflectionToday?.other_reflected
  );
  const mode = normalizeMode(sprintMode);
  const endingSoon = Boolean(
    sprintWindow?.endsToday ||
      (typeof sprintWindow?.daysRemaining === "number" && sprintWindow.daysRemaining <= 1)
  );
  const quietState = ["drift", "reentry", "dormant"].includes(String(explicitState || ""));
  const stateKey =
    endingSoon
      ? "sprint_ending_soon"
      : viewerUpdatedToday && partnerUpdatedToday
        ? "both_moved"
        : partnerUpdatedToday
          ? quietState
            ? "user_drifting_partner_active"
            : "partner_moved_first"
          : viewerUpdatedToday
            ? quietState
              ? "partner_drifting_user_active"
              : "user_moved_first"
            : quietState
              ? "both_drifting"
              : "neither_moved";
  const state = stateKey === "both_drifting" ? "drift" : stateKey === "sprint_ending_soon" ? "active" : resolveSprintState({ explicitState, reflectionToday, viewerActedToday, partnerActedToday });
  const common = { state, stateKey, mode };

  if (stateKey === "partner_moved_first") {
    return {
      ...common,
      chip: "REPORT OPEN",
      title: `${partnerName} submitted today.`,
      body: "Your Daily Report is still open.",
      primaryActionLabel: "Submit report",
      primaryActionRole: "do_move",
      secondaryActionLabel: `Message ${partnerName}`,
      secondaryActionRole: "message_partner",
    };
  }
  if (stateKey === "user_moved_first") {
    return {
      ...common,
      chip: "REPORT SUBMITTED",
      title: "Your Daily Report is in.",
      body: `A quick response could help ${partnerName} submit theirs.`,
      primaryActionLabel: "Open comments",
      primaryActionRole: "open_chat",
      secondaryActionLabel: "",
      secondaryActionRole: "",
    };
  }
  if (stateKey === "both_moved") {
    return {
      ...common,
      chip: "BOTH REPORTED",
      title: "Both reports are in today.",
      body: "That is accountability working.",
      primaryActionLabel: "Send quick win",
      primaryActionRole: "send_quick_win",
      secondaryActionLabel: "View week",
      secondaryActionRole: "view_week",
    };
  }
  if (stateKey === "both_drifting") {
    return {
      ...common,
      chip: "NO REPORTS",
      title: "This accountability is getting quiet.",
      body: "Submit a report or reset so the agreement does not disappear silently.",
      primaryActionLabel: "Submit report",
      primaryActionRole: "do_move",
      secondaryActionLabel: `Message ${partnerName}`,
      secondaryActionRole: "message_partner",
    };
  }
  if (stateKey === "user_drifting_partner_active") {
    return {
      ...common,
      chip: mode === "challenge" ? "KEEP PACE" : "COME BACK",
      title: `${partnerName} is still reporting.`,
      body: "Submit today’s report with Done or Reset.",
      primaryActionLabel: "Submit report",
      primaryActionRole: "do_move",
      secondaryActionLabel: "Reply",
      secondaryActionRole: "open_chat",
    };
  }
  if (stateKey === "partner_drifting_user_active") {
    return {
      ...common,
      chip: "CHECK IN",
      title: `${partnerName} has not reported yet.`,
      body: "A low-pressure note could help.",
      primaryActionLabel: "Open comments",
      primaryActionRole: "open_chat",
      secondaryActionLabel: "",
      secondaryActionRole: "",
    };
  }
  if (stateKey === "sprint_ending_soon") {
    return {
      ...common,
      chip: "ENDING SOON",
      title: sprintWindow?.endsToday ? "This accountability ends today." : "This accountability ends tomorrow.",
      body: "Finish it together with one more report.",
      primaryActionLabel: "Submit report",
      primaryActionRole: "do_move",
      secondaryActionLabel: canRenew ? "Renew Accountability" : `Message ${partnerName}`,
      secondaryActionRole: canRenew ? "renew_sprint" : "message_partner",
    };
  }
  if (stateKey === "neither_moved") {
    return {
      ...common,
      chip: "REPORT DUE",
      title: "No reports submitted yet.",
      body: "Every day ends with a report: Done, Reset, or No report.",
      primaryActionLabel: "Submit report",
      primaryActionRole: "do_move",
      secondaryActionLabel: `Message ${partnerName}`,
      secondaryActionRole: "message_partner",
    };
  }

  return {
    ...common,
    chip: "REPORT DUE",
    title: "No reports submitted yet.",
    body: "Every day ends with a report: Done, Reset, or No report.",
    primaryActionLabel: "Submit report",
    primaryActionRole: "do_move",
    secondaryActionLabel: `Message ${partnerName}`,
    secondaryActionRole: "message_partner",
  };
}

export function buildSprintQuickReplies({ stateKey, sprintMode } = {}) {
  const mode = normalizeMode(sprintMode);
  if (mode === "challenge") {
    return ["Submitting now.", "Report is in.", "Keep pace."];
  }
  if (stateKey === "partner_moved_first" || stateKey === "user_drifting_partner_active") {
    return ["Submitting now.", "I'll do one small step.", "My report is coming."];
  }
  if (stateKey === "partner_drifting_user_active" || stateKey === "both_drifting") {
    return ["Want to submit a reset?", "No pressure — I'm with you.", "Let's restart today."];
  }
  if (stateKey === "both_moved") {
    return ["Both reports are in.", "That counts.", "Same time tomorrow?"];
  }
  return ["I'm with you.", "No pressure — submit a reset?", "Let's keep it simple."];
}

export function buildMomentumRingData(
  reflectionWeek,
  reflectionToday,
  explicitState,
  workspaceSummary = null,
  journeySummary = null,
  personalSummary = null
) {
  const history = Array.isArray(reflectionWeek?.history) ? reflectionWeek.history : [];
  const viewerCheckins = Array.isArray(journeySummary?.viewer_checkins) ? journeySummary.viewer_checkins : [];
  const viewerCheckinDays = new Set((viewerCheckins || []).filter(Boolean)).size;
  const viewerReflectionActiveDays = history.filter(
    (entry) =>
      entry?.viewer_updated ||
      entry?.viewer_noted ||
      entry?.viewer_reflected
  ).length;
  const personalRecentHistory = Array.isArray(personalSummary?.recent_activity_chart)
    ? personalSummary.recent_activity_chart
    : [];
  const personalActiveDaysLast7 =
    Number(personalSummary?.active_days_last_7 ?? 0) ||
    personalRecentHistory.filter(
      (entry) =>
        entry?.active ||
        Number(entry?.checkins || 0) > 0 ||
        Number(entry?.action_count || entry?.actionCount || 0) > 0
    ).length;
  const activeDaysLast7 =
    personalActiveDaysLast7 ||
    Number(workspaceSummary?.viewer_active_days_last_7 ?? 0) ||
    viewerCheckinDays ||
    viewerReflectionActiveDays;
  const personalToday = personalRecentHistory[personalRecentHistory.length - 1] || null;
  const todayActive = Boolean(
    workspaceSummary?.viewer_updated_today ||
      personalToday?.active ||
      Number(personalToday?.checkins || 0) > 0 ||
      Number(personalToday?.action_count || personalToday?.actionCount || 0) > 0 ||
      reflectionToday?.viewer_updated ||
      reflectionToday?.viewer_noted ||
      reflectionToday?.viewer_reflected
  );

  return {
    activeDaysLast7,
    todayActive,
    state: resolveSprintState({ explicitState, reflectionToday }),
  };
}

export function buildRecentMovementItems({
  reflectionToday,
  workspaceSummary = null,
  lastEvent,
  partnerName,
  viewerActedToday = null,
  partnerActedToday = null,
}) {
  const viewerUpdatedToday = viewerActedToday ?? Boolean(
    workspaceSummary?.viewer_updated_today ||
      reflectionToday?.viewer_updated ||
      reflectionToday?.viewer_noted ||
      reflectionToday?.viewer_reflected
  );
  const partnerUpdatedToday = partnerActedToday ?? Boolean(
    workspaceSummary?.partner_updated_today ||
      reflectionToday?.other_updated ||
      reflectionToday?.other_noted ||
      reflectionToday?.other_reflected
  );

  const items = [
    viewerUpdatedToday
      ? {
          id: "viewer-update",
          tone: "positive",
          label: partnerUpdatedToday ? "You submitted today" : "You submitted today. Their report is still open.",
          detail: partnerUpdatedToday ? "Both reports are in today." : "Send a quick response.",
        }
      : {
          id: "viewer-update",
          tone: "warning",
          label: partnerUpdatedToday ? `${partnerName} submitted today. Your report is still open.` : "No reports submitted yet today.",
          detail: "Submit Done or Reset.",
        },
    partnerUpdatedToday
      ? {
          id: "partner-update",
          tone: "positive",
          label: `${partnerName} submitted today`,
          detail: viewerUpdatedToday ? "That is a shared report day." : "Your report is still open.",
        }
      : {
          id: "partner-update",
          tone: "warning",
          label: `${partnerName} has not reported yet`,
          detail: viewerUpdatedToday ? "Send a quick response." : "Submit today’s report.",
        },
    lastEvent?.created_at
      ? {
          id: "last-activity",
          tone: "neutral",
          label: "Last shared activity",
          detail: formatRelativeDayWord(lastEvent.created_at),
        }
      : workspaceSummary?.last_update_at_viewer || workspaceSummary?.last_update_at_other
        ? {
            id: "last-activity",
            tone: "neutral",
            label: "Last shared activity",
            detail: formatRelativeDayWord(
              [workspaceSummary?.last_update_at_viewer, workspaceSummary?.last_update_at_other]
                .filter(Boolean)
                .sort((left, right) => new Date(String(right)).getTime() - new Date(String(left)).getTime())[0]
            ),
          }
      : {
          id: "last-activity",
          tone: "neutral",
          label: "No shared activity yet",
          detail: "Say something quick to start.",
        },
  ];

  return items.slice(0, 3);
}

function getLocalDateKey(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

function startOfCurrentWeek(now = new Date()) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const day = start.getDay();
  const delta = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + delta);
  return start;
}

function parseLocalDateKey(value) {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? null
    : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function dateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

function buildChallengePatternDates({ startDate = null, endDate = null } = {}) {
  const start = parseLocalDateKey(startDate) || startOfCurrentWeek();
  const end = parseLocalDateKey(endDate);
  const dayCount =
    end && end.getTime() >= start.getTime()
      ? Math.max(1, Math.min(7, Math.round((end.getTime() - start.getTime()) / 86400000) + 1))
      : 7;
  return Array.from({ length: dayCount }).map((_, offset) => {
    const date = new Date(start);
    date.setDate(start.getDate() + offset);
    return date;
  });
}

function ensureEntry(map, key) {
  if (!key) return null;
  if (!map.has(key)) {
    map.set(key, {
      date: key,
      viewer_updated: false,
      other_updated: false,
      shared_updated: false,
    });
  }
  return map.get(key);
}

function markEntry(entry, { viewer = false, other = false } = {}) {
  if (!entry) return;
  if (viewer) entry.viewer_updated = true;
  if (other) entry.other_updated = true;
  entry.shared_updated = entry.viewer_updated && entry.other_updated;
}

export function buildWeekPattern(
  reflectionWeek,
  workspaceSummary = null,
  journeySummary = null,
  personalSummary = null,
  challengeWindow = {}
) {
  const viewerCheckins = Array.isArray(journeySummary?.viewer_checkins) ? journeySummary.viewer_checkins : [];
  const partnerCheckins = Array.isArray(journeySummary?.other_checkins) ? journeySummary.other_checkins : [];
  const reflectionHistory = Array.isArray(reflectionWeek?.history) ? reflectionWeek.history : [];
  const workspaceHistory = Array.isArray(workspaceSummary?.history) ? workspaceSummary.history : [];
  const personalWeekHistory = Array.isArray(personalSummary?.current_week_activity_chart)
    ? personalSummary.current_week_activity_chart
    : [];
  const byDate = new Map();

  viewerCheckins.forEach((value) => {
    const key = String(value || "");
    markEntry(ensureEntry(byDate, key), { viewer: true });
  });

  partnerCheckins.forEach((value) => {
    const key = String(value || "");
    markEntry(ensureEntry(byDate, key), { other: true });
  });

  reflectionHistory.forEach((entry) => {
    const key = String(entry?.date || "");
    markEntry(ensureEntry(byDate, key), {
      viewer: Boolean(entry?.viewer_updated || entry?.viewer_noted || entry?.viewer_reflected),
      other: Boolean(entry?.other_updated || entry?.other_noted || entry?.other_reflected),
    });
  });

  workspaceHistory.forEach((entry) => {
    const key = String(entry?.date || "");
    markEntry(ensureEntry(byDate, key), {
      viewer: Boolean(entry?.viewer_updated),
      other: Boolean(entry?.other_updated),
    });
  });

  personalWeekHistory.forEach((entry) => {
    const key = String(entry?.date || "");
    const viewerActive = Boolean(
      entry?.active || Number(entry?.checkins || 0) > 0 || Number(entry?.action_count || entry?.actionCount || 0) > 0
    );
    markEntry(ensureEntry(byDate, key), { viewer: viewerActive });
  });

  const patternDates = buildChallengePatternDates(challengeWindow);
  const items = [];
  patternDates.forEach((date) => {
    const key = dateKey(date);
    const entry = byDate.get(key) || null;
    items.push({
      key,
      label: date.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 1),
      viewerActive: Boolean(entry?.viewer_updated || entry?.viewer_noted || entry?.viewer_reflected),
      partnerActive: Boolean(entry?.other_updated || entry?.other_noted || entry?.other_reflected),
      sharedActive: Boolean(
        (entry?.viewer_updated || entry?.viewer_noted || entry?.viewer_reflected) &&
          (entry?.other_updated || entry?.other_noted || entry?.other_reflected)
      ),
    });
  });
  const viewerLastKey = getLocalDateKey(workspaceSummary?.last_update_at_viewer);
  const partnerLastKey = getLocalDateKey(workspaceSummary?.last_update_at_other);

  return items.map((item) => {
    const viewerActive = item.viewerActive || Boolean(viewerLastKey && item.key === viewerLastKey);
    const partnerActive = item.partnerActive || Boolean(partnerLastKey && item.key === partnerLastKey);
    return {
      ...item,
      viewerActive,
      partnerActive,
      sharedActive: viewerActive && partnerActive,
    };
  });
}
